/*
 * The arithmetic of dragging a card sideways to reveal what lies under it
 * (Home's isolate cards and their Lab analytics tray, hooks/useCardReveal.js;
 * docs/UI_HUBS_AND_GESTURES_PLAN.md section 2). Pure: no DOM, no clock.
 *
 * Every distance is PHYSICAL px, positive to the right, in Hebrew as in
 * English (decision D12): a finger moving left to right opens the tray, which
 * lies at the card's physical left; right to left closes it.
 */

/** How far beyond its stops a card still follows the finger, at most (px). */
export const RUBBER_LIMIT = 32;
// How stiff the rubber band is: the larger, the more of the first px past a stop are followed.
const RUBBER_STIFFNESS = 0.55;
/** Of the tray's width, how far a slow drag must carry the card for it to stay where it was pulled. */
export const SETTLE_FRACTION = 0.4;
/** A flick at least this fast (px/ms) settles the card the way it was flicked, however short. */
export const FLICK_SPEED = 0.5;

/**
 * Which way a stroke is going, once it has gone far enough to tell: 'x' when
 * it runs more than 1.5 times as far across as down, 'y' for the reverse,
 * null while it is still within `slop` px of where it began or runs too
 * diagonally to call.
 */
export function lockAxis(dx, dy, slop = 8) {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (!Number.isFinite(ax) || !Number.isFinite(ay)) return null;
  if (ax <= slop && ay <= slop) return null;
  if (ax > 1.5 * ay) return 'x';
  if (ay > 1.5 * ax) return 'y';
  return null;
}

/** The distance followed for `excess` px past a stop: about all of the first few, never more than RUBBER_LIMIT. */
function rubber(excess) {
  return (1 - 1 / ((excess * RUBBER_STIFFNESS) / RUBBER_LIMIT + 1)) * RUBBER_LIMIT;
}

/**
 * Where the card is drawn (its translateX, px) while the finger is `dx` px
 * from where it landed: from rest (0) or from open (`trayWidth`), followed
 * one for one between the two, and on a rubber band beyond either.
 */
export function revealOffset(dx, trayWidth, open = false) {
  const width = Math.max(0, Number(trayWidth) || 0);
  const raw = (open ? width : 0) + (Number(dx) || 0);
  if (raw < 0) return -rubber(-raw);
  if (raw > width) return width + rubber(raw - width);
  return raw;
}

/**
 * Whether the card rests open when the finger lifts: `dx` px from where it
 * landed, last moving at `vx` px/ms (positive to the right), from closed or
 * `open`. A flick faster than FLICK_SPEED goes the way it was flicked;
 * otherwise a closed card opens once pulled SETTLE_FRACTION of the tray's
 * width, and an open one closes once pushed back as far. Anything less goes
 * back to where it was.
 */
export function settle({ dx = 0, vx = 0, trayWidth = 0, open = false } = {}) {
  const speed = Number(vx) || 0;
  if (speed > FLICK_SPEED) return true;
  if (speed < -FLICK_SPEED) return false;
  const width = Math.max(0, Number(trayWidth) || 0);
  const moved = Number(dx) || 0;
  const far = SETTLE_FRACTION * width;
  if (open) return !(-moved >= far && width > 0);
  return width > 0 && moved >= far;
}
